import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, RefreshCw, Wrench } from 'lucide-react';
import type { VinculoRevisadoVista } from '@shared/types';

/**
 * Sprint 60 — Revisión de ingredientes (DT-038).
 *
 * Existe por el "Refresco 600 ml → Frijoles Charros": cada refresco vendido
 * descontaba frijoles, tres paquetes lo llevaban, y pasó meses inadvertido porque
 * el dato era válido para el sistema. Nada sabía que un refresco no se hace con
 * frijoles.
 *
 * Dos decisiones de diseño:
 *
 *  1. **Si no hay nada que revisar, la pantalla lo dice y se calla.** Una revisión
 *     que siempre muestra algo se vuelve ruido de fondo y se deja de mirar.
 *  2. **Traduce en vez de juzgar.** No inventa umbrales de "gramaje absurdo": pone
 *     la frase *"1 pieza vendida consume 10 kg de Pepsi"* y deja que la persona
 *     vea el error. Eso encontró una lata configurada para consumir 10 kg.
 */
export function RevisionInsumos() {
  const [vinculos, setVinculos] = useState<VinculoRevisadoVista[] | null>(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    const res = await window.api.productos.revisionInsumos();
    if (res.ok) setVinculos(res.data);
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (cargando && vinculos === null) {
    return <div className="card text-slate-500">Revisando los ingredientes…</div>;
  }

  const graves = (vinculos ?? []).filter((v) => v.sospechas.some((s) => s.nivel === 'alta'));

  return (
    <div className="card space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-semibold">
            <Wrench className="h-5 w-5 text-primary" />
            Revisión de ingredientes
          </h3>
          <p className="mt-0.5 text-sm text-slate-600">
            Comprueba que cada producto descuente del ingrediente correcto. Un producto ligado al
            ingrediente equivocado descuenta del inventario que no le toca, y eso no se nota hasta
            que las cuentas no cuadran.
          </p>
        </div>
        <button onClick={() => void cargar()} className="btn-ghost shrink-0 p-2" aria-label="Volver a revisar">
          <RefreshCw className={`h-5 w-5 ${cargando ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Sin hallazgos: se dice y se calla. */}
      {(vinculos ?? []).length === 0 ? (
        <p className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm font-medium text-green-900">
          <Check className="h-5 w-5" />
          Todo en orden: cada producto descuenta de un ingrediente que le corresponde.
        </p>
      ) : (
        <>
          {graves.length > 0 && (
            <p className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900">
              {graves.length === 1
                ? '1 producto necesita tu atención.'
                : `${graves.length} productos necesitan tu atención.`}{' '}
              Los demás son sólo para verificar.
            </p>
          )}

          <ul className="space-y-2">
            {(vinculos ?? []).map((v) => {
              const grave = v.sospechas.some((s) => s.nivel === 'alta');
              return (
                <li
                  key={v.productoId}
                  className={`rounded-lg border px-3 py-2 ${
                    grave ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <span className="font-semibold">{v.productoNombre}</span>
                    <span className="text-slate-400">descuenta de</span>
                    <span className="font-medium">{v.insumoNombre}</span>
                    {!v.habilitado && (
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                        ya no se vende
                      </span>
                    )}
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {v.sospechas.map((s, i) => (
                      <li
                        key={i}
                        className={`flex items-start gap-1.5 text-xs ${
                          s.nivel === 'alta' ? 'text-amber-900' : 'text-slate-600'
                        }`}
                      >
                        {s.nivel === 'alta' && <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                        <span>{s.mensaje}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>

          <p className="text-xs text-slate-500">
            Si un aviso no aplica —hay ingredientes que se llaman distinto al producto, como
            “Guacamole” hecho de “Aguacate”— déjalo así. Esto avisa, no corrige nada por su cuenta.
          </p>
        </>
      )}
    </div>
  );
}
